import { ChevronsLeftRight } from "lucide-react";
import { useState } from "react";
import { PlaceholderBadge } from "./PlaceholderBadge";

interface CompareImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}

// A before→after wipe answering the "is it really my pet?" doubt directly: the
// photo you send vs. the pillow cut to their outline. The interactive control is
// a real <input type="range"> laid over the frame, so dragging, tapping, and
// arrow-key nudging all work and it's announced to assistive tech — the visible
// line + handle are decorative and follow the value. Clip-path (not a width hack)
// keeps both images full-bleed and undistorted.
export function BeforeAfter({
  before,
  after,
  beforeLabel,
  afterLabel,
  sliderLabel,
  afterPlaceholder = false,
}: {
  before: CompareImage;
  after: CompareImage;
  beforeLabel: string;
  afterLabel: string;
  sliderLabel: string;
  afterPlaceholder?: boolean;
}) {
  const [position, setPosition] = useState(50);
  return (
    <figure className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-border bg-secondary shadow-sm sm:aspect-square">
      <img
        src={after.src}
        alt={after.alt}
        width={after.width}
        height={after.height}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <img
        src={before.src}
        alt={before.alt}
        width={before.width}
        height={before.height}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      />

      {afterPlaceholder ? <PlaceholderBadge className="left-auto right-2" /> : null}

      <figcaption className="pointer-events-none absolute inset-0">
        <span className="absolute left-3 top-3 rounded-full bg-foreground/70 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
          {beforeLabel}
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground">
          {afterLabel}
        </span>
      </figcaption>

      {/* The accessible control (peer), invisible but full-frame: drag/tap/arrows. */}
      <input
        type="range"
        min={0}
        max={100}
        value={position}
        onChange={(event) => setPosition(Number(event.target.value))}
        aria-label={sliderLabel}
        data-testid="before-after-range"
        className="peer absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
      />

      {/* Visible handle, following the value; gets the focus ring from the peer. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 z-10"
        style={{ left: `${position}%` }}
      >
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.25)]" />
        <div className="absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-md ring-ring ring-offset-2 peer-focus-visible:ring-2">
          <ChevronsLeftRight className="h-4 w-4" />
        </div>
      </div>
    </figure>
  );
}
